/**
 * FrontDesk Route Parser
 *
 * Parses Next.js router query parameters into strongly-typed FrontDesk routes.
 * Handles all URL patterns and query parameters for deep linking.
 */

import {
  ParsedRoute,
  FrontDeskRoute,
  RouteType,
  FrontDeskChannel,
  FrontDeskSection,
  TicketView,
  RouteQueryParams,
  ChatwootView,
  ChatwootRoute,
  ChatwootFilters,
  ChatwootSettingsSubpage,
} from "./types";

/**
 * Next.js router query interface
 */
export interface NextRouterQuery {
  frontdeskRoute?: string | string[];
  provider?: string | string[];
  view?: string | string[];
  ticket?: string | string[];
  conversation?: string | string[];
  channel?: string | string[];
  // OAuth callback parameters
  accountId?: string | string[];
  inboxId?: string | string[];
  // Chatwoot filter parameters
  status?: string | string[];
  statusId?: string | string[];
  assigneeType?: string | string[];
  teamId?: string | string[];
  sortBy?: string | string[];
  search?: string | string[];
  labels?: string | string[];
  lifecycle?: string | string[];
  // Settings subpage
  subpage?: string | string[];
  // Customers view selection
  customer?: string | string[];
  sender?: string | string[];
  openConversation?: string | string[];
}

/**
 * Normalize query parameter to string or undefined
 */
const normalizeParam = (param: string | string[] | undefined): string | undefined => {
  if (Array.isArray(param)) return param[0];
  return param;
};

/**
 * Check if a string is a valid ticket ID format
 * Supports three formats:
 * 1. TICKET-XXXXX (legacy display format)
 * 2. Numeric ID (e.g., 12345)
 * 3. UUID (e.g., 846c2a13-4133-4046-b04a-e3607f8e45b6)
 */
const isTicketId = (value: string): boolean => {
  // Format 1: TICKET-XXXXX (legacy display format)
  if (value.startsWith("TICKET-")) return true;

  // Format 2: Pure numeric ID
  if (/^\d+$/.test(value)) return true;

  // Format 3: UUID (8-4-4-4-12 format)
  // Matches: 846c2a13-4133-4046-b04a-e3607f8e45b6
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidPattern.test(value)) return true;

  return false;
};

/**
 * Parse frontdeskRoute segment to determine route type and parameters
 */
const parseFrontDeskRoute = (frontdeskRoute?: string): Partial<FrontDeskRoute> => {
  if (!frontdeskRoute || frontdeskRoute === "inbox") {
    return { type: RouteType.INBOX };
  }

  // Check if it's a ticket route
  if (frontdeskRoute === "tickets") {
    return { type: RouteType.TICKET_LIST };
  }

  // Check if it's a Chatwoot route
  if (frontdeskRoute === "chatwoot") {
    return { type: RouteType.CHATWOOT };
  }

  // Check if it's a section route
  if (frontdeskRoute === "assigned") {
    return { type: RouteType.SECTION, section: FrontDeskSection.ASSIGNED };
  }
  if (frontdeskRoute === "admin") {
    return { type: RouteType.SECTION, section: FrontDeskSection.ADMIN };
  }
  if (frontdeskRoute === "settings") {
    return { type: RouteType.SECTION, section: FrontDeskSection.SETTINGS };
  }

  // Default to inbox
  return { type: RouteType.INBOX };
};

/**
 * Parse Chatwoot view from query param
 */
const parseChatwootView = (viewParam?: string): ChatwootView => {
  if (!viewParam) {
    return ChatwootView.DASHBOARD;
  }

  // Map query param values to ChatwootView enum
  const viewMap: Record<string, ChatwootView> = {
    dashboard: ChatwootView.DASHBOARD,
    "my-inbox": ChatwootView.MY_INBOX,
    "all-conversations": ChatwootView.ALL_CONVERSATIONS,
    mentions: ChatwootView.MENTIONS,
    unattended: ChatwootView.UNATTENDED,
    "direct-messages": ChatwootView.DIRECT_MESSAGES,
    customers: ChatwootView.CUSTOMERS,
    channels: ChatwootView.CHANNELS,
    workboard: ChatwootView.WORKBOARD,
    "webhook-test": ChatwootView.WEBHOOK_TEST,
    settings: ChatwootView.SETTINGS,
    "oauth-callback": ChatwootView.OAUTH_CALLBACK,
  };

  return viewMap[viewParam] || ChatwootView.DASHBOARD;
};

/**
 * Parse Chatwoot settings subpage from query param
 */
const parseChatwootSettingsSubpage = (subpageParam?: string): ChatwootSettingsSubpage | undefined => {
  if (!subpageParam) {
    return undefined;
  }

  // Map query param values to ChatwootSettingsSubpage enum
  const subpageMap: Record<string, ChatwootSettingsSubpage> = {
    general: ChatwootSettingsSubpage.GENERAL,
    agents: ChatwootSettingsSubpage.AGENTS,
    inboxes: ChatwootSettingsSubpage.INBOXES,
    teams: ChatwootSettingsSubpage.TEAMS,
    labels: ChatwootSettingsSubpage.LABELS,
    statuses: ChatwootSettingsSubpage.STATUSES,
    "conversation-expiration": ChatwootSettingsSubpage.CONVERSATION_EXPIRATION,
    "email-templates": ChatwootSettingsSubpage.EMAIL_TEMPLATES,
    automations: ChatwootSettingsSubpage.AUTOMATIONS,
    warnings: ChatwootSettingsSubpage.WARNINGS,
    profile: ChatwootSettingsSubpage.PROFILE,
    "webhook-test": ChatwootSettingsSubpage.WEBHOOK_TEST,
  };

  return subpageMap[subpageParam];
};

/**
 * Parse provider segment to determine channel or ticket details
 */
const parseProvider = (provider?: string, baseRoute?: Partial<FrontDeskRoute>): Partial<FrontDeskRoute> => {
  if (!provider) return baseRoute || {};

  // If we're in Chatwoot route context, view is now in query params
  // So we don't parse from provider segment anymore
  if (baseRoute?.type === RouteType.CHATWOOT) {
    return baseRoute; // Return as-is, view will be parsed from query params
  }

  // If we're in ticket route context
  if (baseRoute?.type === RouteType.TICKET_LIST) {
    // Check if provider is a ticket ID
    if (isTicketId(provider)) {
      return {
        type: RouteType.TICKET_DETAIL,
        ticketId: provider,
      } as Partial<FrontDeskRoute>;
    }

    // Otherwise it's a ticket view
    const ticketViewMap: Record<string, TicketView> = {
      "my-tickets": TicketView.MY_TICKETS,
      unassigned: TicketView.UNASSIGNED,
      "all-tickets": TicketView.ALL_TICKETS,
      done: TicketView.DONE,
      archived: TicketView.ARCHIVED,
    };

    const view = ticketViewMap[provider];
    if (view) {
      return {
        type: RouteType.TICKET_LIST,
        view,
      } as Partial<FrontDeskRoute>;
    }
  }

  // Check if provider is a channel
  const channelMap: Record<string, FrontDeskChannel> = {
    unified: FrontDeskChannel.ALL,
    email: FrontDeskChannel.EMAIL,
    whatsapp: FrontDeskChannel.WHATSAPP,
    internal: FrontDeskChannel.INTERNAL,
    chatwoot: FrontDeskChannel.CHATWOOT,
  };

  const channel = channelMap[provider];
  if (channel) {
    return {
      type: RouteType.CHANNEL,
      channel,
    } as Partial<FrontDeskRoute>;
  }

  return baseRoute || {};
};

/**
 * Parse Chatwoot filter parameters from query
 */
const parseChatwootFilters = (query: NextRouterQuery): ChatwootFilters | undefined => {
  const filters: ChatwootFilters = {};
  let hasFilters = false;

  if (query.status) {
    filters.status = normalizeParam(query.status);
    hasFilters = true;
  }
  if (query.statusId) {
    filters.statusId = normalizeParam(query.statusId);
    hasFilters = true;
  }
  if (query.assigneeType) {
    filters.assigneeType = normalizeParam(query.assigneeType);
    hasFilters = true;
  }
  if (query.inboxId) {
    filters.inboxId = normalizeParam(query.inboxId);
    hasFilters = true;
  }
  if (query.teamId) {
    filters.teamId = normalizeParam(query.teamId);
    hasFilters = true;
  }
  if (query.sortBy) {
    filters.sortBy = normalizeParam(query.sortBy);
    hasFilters = true;
  }
  if (query.search) {
    filters.search = normalizeParam(query.search);
    hasFilters = true;
  }
  if (query.labels) {
    filters.labels = normalizeParam(query.labels);
    hasFilters = true;
  }
  if (normalizeParam(query.lifecycle) === "archived") {
    filters.lifecycle = "archived";
    hasFilters = true;
  }

  return hasFilters ? filters : undefined;
};

/**
 * Parse query parameters for additional context
 */
const parseQueryParams = (query: NextRouterQuery): RouteQueryParams & { channel?: string } => {
  return {
    view: normalizeParam(query.view),
    ticket: normalizeParam(query.ticket),
    conversation: normalizeParam(query.conversation),
    channel: normalizeParam(query.channel),
  };
};

/**
 * Apply query parameter overrides to route
 * Query params (from notifications) take precedence
 */
const applyQueryParamOverrides = (
  route: Partial<FrontDeskRoute>,
  queryParams: RouteQueryParams & { channel?: string },
  query: NextRouterQuery
): FrontDeskRoute => {
  // Handle Chatwoot-specific query params
  if (route.type === RouteType.CHATWOOT) {
    const chatwootRoute = route as Partial<ChatwootRoute>;
    // Parse view from query params, fallback to route view or default
    const view = queryParams.view ? parseChatwootView(queryParams.view) : chatwootRoute.view || ChatwootView.DASHBOARD;

    // Parse filters from query params
    const filters = parseChatwootFilters(query);

    // Parse settings subpage if view is SETTINGS
    const settingsSubpage =
      view === ChatwootView.SETTINGS ? parseChatwootSettingsSubpage(normalizeParam(query.subpage)) : undefined;

    const customerId = view === ChatwootView.CUSTOMERS ? normalizeParam(query.customer)?.trim() || undefined : undefined;
    const senderId =
      view === ChatwootView.CUSTOMERS && !customerId ? normalizeParam(query.sender)?.trim() || undefined : undefined;
    const drawerConversationId =
      customerId || senderId ? normalizeParam(query.openConversation)?.trim() || undefined : undefined;

    return {
      type: RouteType.CHATWOOT,
      view,
      conversationId: queryParams.conversation || chatwootRoute.conversationId,
      channelId: queryParams.channel || chatwootRoute.channelId,
      filters: filters || chatwootRoute.filters,
      settingsSubpage: settingsSubpage || chatwootRoute.settingsSubpage,
      customerId,
      senderId,
      drawerConversationId,
    };
  }

  // Handle view query param (from notifications)
  if (queryParams.view) {
    // Check if it's a channel view
    const channelMap: Record<string, FrontDeskChannel> = {
      internal: FrontDeskChannel.INTERNAL,
      email: FrontDeskChannel.EMAIL,
      whatsapp: FrontDeskChannel.WHATSAPP,
    };

    if (channelMap[queryParams.view]) {
      const channelRoute: ChannelRoute = {
        type: RouteType.CHANNEL,
        channel: channelMap[queryParams.view],
        conversationId: queryParams.conversation,
      };
      return channelRoute;
    }

    // Check if it's a ticket view
    const ticketViewMap: Record<string, TicketView> = {
      "my-tickets": TicketView.MY_TICKETS,
      unassigned: TicketView.UNASSIGNED,
      "all-tickets": TicketView.ALL_TICKETS,
      done: TicketView.DONE,
      archived: TicketView.ARCHIVED,
    };

    if (ticketViewMap[queryParams.view]) {
      const ticketRoute: TicketListRoute = {
        type: RouteType.TICKET_LIST,
        view: ticketViewMap[queryParams.view],
      };
      return ticketRoute;
    }
  }

  // Handle ticket query param (from notifications)
  if (queryParams.ticket) {
    const ticketRoute: TicketDetailRoute = {
      type: RouteType.TICKET_DETAIL,
      ticketId: queryParams.ticket,
      view: (route as any).view, // Preserve current view if available
    };
    return ticketRoute;
  }

  // Handle conversation query param
  if (queryParams.conversation && route.type === RouteType.CHANNEL) {
    return {
      ...route,
      conversationId: queryParams.conversation,
    } as ChannelRoute;
  }

  // Return route with defaults if needed
  return ensureValidRoute(route);
};

/**
 * Ensure route has all required fields with defaults
 */
const ensureValidRoute = (route: Partial<FrontDeskRoute>): FrontDeskRoute => {
  if (route.type === RouteType.INBOX) {
    return { type: RouteType.INBOX };
  }

  if (route.type === RouteType.CHANNEL) {
    return {
      type: RouteType.CHANNEL,
      channel: (route as any).channel || FrontDeskChannel.ALL,
      conversationId: (route as any).conversationId,
    };
  }

  if (route.type === RouteType.TICKET_LIST) {
    return {
      type: RouteType.TICKET_LIST,
      view: (route as any).view || TicketView.ALL_TICKETS,
    };
  }

  if (route.type === RouteType.TICKET_DETAIL) {
    return {
      type: RouteType.TICKET_DETAIL,
      ticketId: (route as any).ticketId || "",
      view: (route as any).view,
    };
  }

  if (route.type === RouteType.SECTION) {
    return {
      type: RouteType.SECTION,
      section: (route as any).section || FrontDeskSection.SETTINGS,
    };
  }

  if (route.type === RouteType.CHATWOOT) {
    return {
      type: RouteType.CHATWOOT,
      view: (route as any).view || ChatwootView.DASHBOARD,
      conversationId: (route as any).conversationId,
      channelId: (route as any).channelId,
      settingsSubpage: (route as any).settingsSubpage,
    };
  }

  // Default fallback
  return { type: RouteType.INBOX };
};

/**
 * Default route to use when parsing fails or for invalid URLs
 * Falls back to Chatwoot dashboard as the safest default
 */
const DEFAULT_PARSED_ROUTE: ParsedRoute = {
  route: { type: RouteType.CHATWOOT, view: ChatwootView.DASHBOARD },
  queryParams: {},
};

/**
 * Main parser function - converts Next.js query to FrontDesk route
 *
 * URL Patterns:
 * - /frontdesk/inbox → Inbox (unified dashboard)
 * - /frontdesk/inbox/email → Email channel
 * - /frontdesk/inbox/internal?conversation=123 → DM with conversation
 * - /frontdesk/tickets/my-tickets → Ticket list view
 * - /frontdesk/tickets/TICKET-123 → Ticket detail
 * - /frontdesk/assigned → Assigned section
 * - /frontdesk/inbox?view=email&conversation=456 → Deep link from notification
 *
 * @param query - Next.js router query object
 * @returns Parsed route with safe defaults on parsing errors
 */
export const parseRoute = (query: NextRouterQuery): ParsedRoute => {
  try {
    // Guard against null/undefined query object
    if (!query || typeof query !== "object") {
      console.warn("[RouteParser] Invalid query object, using default route");
      return DEFAULT_PARSED_ROUTE;
    }

    const frontdeskRoute = normalizeParam(query.frontdeskRoute);
    const provider = normalizeParam(query.provider);
    const queryParams = parseQueryParams(query);

    // Parse base route from frontdeskRoute segment
    let route = parseFrontDeskRoute(frontdeskRoute);

    // Parse provider segment if present
    route = parseProvider(provider, route);

    // Apply query parameter overrides (from notifications) and parse filters
    const finalRoute = applyQueryParamOverrides(route, queryParams, query);

    return {
      route: finalRoute,
      queryParams,
    };
  } catch (error) {
    // Log error for debugging but don't crash the app
    console.error("[RouteParser] Failed to parse route:", error, {
      query: JSON.stringify(query, null, 2),
    });

    // Return safe default route to prevent app crash
    return DEFAULT_PARSED_ROUTE;
  }
};

/**
 * Helper: Get current section from route (for backward compatibility)
 */
export const getSectionFromRoute = (route: FrontDeskRoute): "inbox" | "assigned" | "admin" | "settings" => {
  if (route.type === RouteType.SECTION) {
    return route.section as any;
  }
  return "inbox";
};

/**
 * Helper: Get current channel from route (for backward compatibility)
 */
export const getChannelFromRoute = (route: FrontDeskRoute): "all" | "email" | "whatsapp" | "internal" | "chatwoot" => {
  if (route.type === RouteType.CHANNEL) {
    return route.channel as any;
  }
  if (route.type === RouteType.CHATWOOT) {
    return "chatwoot";
  }
  if (route.type === RouteType.INBOX) {
    return "all";
  }
  // For ticket routes or other sections, return 'all'
  return "all";
};

/**
 * Helper: Get ticket view from route (for backward compatibility)
 */
export const getTicketViewFromRoute = (route: FrontDeskRoute): TicketView | null => {
  if (route.type === RouteType.TICKET_LIST) {
    return route.view;
  }
  if (route.type === RouteType.TICKET_DETAIL) {
    return route.view || null;
  }
  return null;
};

/**
 * Helper: Check if route is in ticket mode
 */
export const isTicketRoute = (route: FrontDeskRoute): boolean => {
  return route.type === RouteType.TICKET_LIST || route.type === RouteType.TICKET_DETAIL;
};

import { InboxRoute, ChannelRoute, TicketListRoute, TicketDetailRoute } from "./types";
